// Sincronizacao do estado de picking entre dispositivos.
//
// Todo o progresso do picking vive no localStorage (isolado por navegador).
// Este modulo espelha esses dados num endpoint no servidor de voces
// (picking-extras/state.php) para que outro aparelho retome de onde parou e
// dois aparelhos fiquem quase ao vivo.
//
// Degrada graciosamente: se o endpoint nao existir/estiver fora do ar, o app
// funciona exatamente como hoje (so localStorage), sem erros ao usuario.

const ENDPOINT = '/picking-extras/state.php';

// Mapas por-pedido: { [orderId]: { [itemId]: valor } } — merge no nivel do pedido.
const OPERATOR_ORDER_MAPS = ['pickedSelections', 'editedQtyByOrder', 'revealedLocationsByOrder'];
// Mapas planos do operador: { [chave]: valor } — merge por chave.
const OPERATOR_FLAT_MAPS = ['holdFoundLocal'];
// Mapa global (compartilhado entre todos os operadores).
const GLOBAL_FLAT_MAPS = ['warehouseLocBySku'];

const OPERATOR_KEYS = [...OPERATOR_ORDER_MAPS, ...OPERATOR_FLAT_MAPS];
const ALL_SYNC_KEYS = [...OPERATOR_KEYS, ...GLOBAL_FLAT_MAPS];

const PUSH_DEBOUNCE_MS = 1200;
// 30s (era 5s): com 8 dispositivos de aba aberta o dia todo, 5s gerava ~12k
// requests/dia. 30s + pausa em aba escondida corta isso ~10x, e a troca entre
// aparelhos continua rapida o suficiente para o fluxo real (trocar de tablet).
const POLL_INTERVAL_MS = 30000;

let started = false;
let disabled = false; // vira true se o endpoint nao estiver publicado (404)
let suppress = false; // evita loop quando aplicamos dados do servidor
let pushTimer = null;
let pushPending = false;
let inFlight = null;
let pollTimer = null;
let origSetItem = null;

const getOperator = () => {
  try {
    return (localStorage.getItem('operator') || localStorage.getItem('username') || '').trim();
  } catch {
    return '';
  }
};

const hasSession = () => {
  try {
    return localStorage.getItem('authed') === '1';
  } catch {
    return false;
  }
};

const readMap = (key) => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

// Escreve no localStorage sem disparar um novo push (aplicando dados do servidor).
const writeMapSilently = (key, obj) => {
  suppress = true;
  try {
    origSetItem(key, JSON.stringify(obj));
    // Notifica paginas montadas que escutam 'storage' (ex.: HoldItemsPage).
    window.dispatchEvent(new StorageEvent('storage', { key, newValue: JSON.stringify(obj) }));
  } catch (err) {
    console.debug('sync: writeMapSilently failed', err);
  } finally {
    suppress = false;
  }
};

const endpointUrl = () => {
  const op = encodeURIComponent(getOperator() || 'shared');
  return `${ENDPOINT}?operator=${op}`;
};

// Sem header de Authorization: o token vive num cookie httpOnly que o navegador
// envia sozinho em requests same-origin; o state.php le o cookie no servidor.

// Aplica o doc do servidor por cima do local (server ∪ local).
const applyServerDoc = (doc) => {
  if (!doc || typeof doc !== 'object') return;
  const data = doc.data && typeof doc.data === 'object' ? doc.data : {};

  OPERATOR_ORDER_MAPS.forEach((key) => {
    const local = readMap(key);
    const server = data[key] && typeof data[key] === 'object' ? data[key] : {};
    const merged = { ...local };
    // Merge no nivel do pedido: o servidor vence por pedido (ja inclui o que
    // este aparelho enviou); pedidos so-locais ficam.
    Object.keys(server).forEach((orderId) => { merged[orderId] = server[orderId]; });
    writeMapSilently(key, merged);
  });

  OPERATOR_FLAT_MAPS.forEach((key) => {
    const local = readMap(key);
    const server = data[key] && typeof data[key] === 'object' ? data[key] : {};
    writeMapSilently(key, { ...local, ...server });
  });

  const warehouse = doc.warehouse && typeof doc.warehouse === 'object' ? doc.warehouse : {};
  GLOBAL_FLAT_MAPS.forEach((key) => {
    const local = readMap(key);
    writeMapSilently(key, { ...local, ...warehouse });
  });
};

const buildPayload = () => {
  const data = {};
  OPERATOR_KEYS.forEach((key) => { data[key] = readMap(key); });
  const warehouse = {};
  GLOBAL_FLAT_MAPS.forEach((key) => { Object.assign(warehouse, readMap(key)); });
  return { data, warehouse };
};

const pushNow = async () => {
  if (disabled) return;
  pushPending = false;
  if (!hasSession()) return;
  try {
    const res = await fetch(endpointUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(buildPayload()),
    });
    if (res.status === 404) { disabled = true; return; }
    // Nao reaplicamos a resposta aqui para nao pisar em edicoes feitas durante
    // o POST; a reconciliacao acontece no proximo pull.
  } catch (err) {
    console.debug('sync: push failed (offline?)', err);
  }
};

const flushPush = async () => {
  if (pushTimer) { clearTimeout(pushTimer); pushTimer = null; }
  if (pushPending) await pushNow();
};

const schedulePush = () => {
  if (disabled) return;
  pushPending = true;
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => { pushTimer = null; pushNow(); }, PUSH_DEBOUNCE_MS);
};

const pullNow = async () => {
  if (disabled) return;
  if (!hasSession()) return;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const res = await fetch(endpointUrl(), { method: 'GET', credentials: 'same-origin' });
      if (res.status === 404) { disabled = true; return; }
      if (!res.ok) return;
      // Se nao for JSON, o endpoint nao esta publicado (ex.: dev serve o
      // index.html do SPA). Desativa em silencio para nao poluir o console.
      const ctype = res.headers.get('content-type') || '';
      if (!ctype.includes('application/json')) { disabled = true; return; }
      const doc = await res.json();
      applyServerDoc(doc);
    } catch (err) {
      console.debug('sync: pull failed (offline?)', err);
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
};

const reconcile = async () => {
  await flushPush();
  await pullNow();
};

// Aba escondida (tablet na gaveta, outra aba em foco): nao ha o que sincronizar
// para uma tela que ninguem ve. Ao voltar a ficar visivel, reconcilia na hora.
const isHidden = () => typeof document !== 'undefined' && document.hidden;

const onVisibilityChange = () => {
  if (!isHidden()) reconcile();
};

export const initPickingSync = () => {
  if (started || typeof window === 'undefined') return;
  started = true;

  // Intercepta escritas no localStorage para agendar o push automaticamente,
  // sem precisar tocar em cada pagina.
  origSetItem = window.localStorage.setItem.bind(window.localStorage);
  window.localStorage.setItem = function patchedSetItem(key, value) {
    origSetItem(key, value);
    if (!suppress && ALL_SYNC_KEYS.includes(key)) schedulePush();
  };

  // Puxa o estado assim que abre (retomar em outro aparelho).
  pullNow();

  pollTimer = setInterval(() => {
    if (isHidden()) return; // aba escondida: pula o ciclo (nada de request)
    reconcile();
  }, POLL_INTERVAL_MS);

  window.addEventListener('focus', reconcile);
  window.addEventListener('online', reconcile);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('beforeunload', () => { flushPush(); });
};

export const stopPickingSync = () => {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  window.removeEventListener('focus', reconcile);
  window.removeEventListener('online', reconcile);
  document.removeEventListener('visibilitychange', onVisibilityChange);
};
